import { Dispatch, SetStateAction, useEffect, useState } from 'react';

// ToDo:
// Add Error handling for Copyto Clipboard
// Escape button to close modal

type PreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  defaultAR?: string;
  updateAspectRatio?: Dispatch<SetStateAction<number>>;
};

export const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  defaultAR,
  children,
  updateAspectRatio,
}) => {
  const [aspectRatio, setAspectRatio] = useState(defaultAR || '4:3');
  const [ratio, setRatio] = useState(4 / 3);

  const availableAspectRatios: { [key: string]: number } = {
    '1:1': 1,
    '3:2': 3 / 2,
    '4:3': 4 / 3,
    '3:4': 3 / 4,
    '16:9': 16 / 9,
    // Wide option
    '7:3': 7 / 3,
  };

  useEffect(() => {
    const ratio = availableAspectRatios[aspectRatio];
    setRatio(ratio);
    updateAspectRatio && updateAspectRatio(ratio);
  }, [aspectRatio]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Add event listener when modal is open
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    // Clean up event listener when component unmounts or modal closes
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const onOutsideModalClicked = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const aspectRatioControl = (
    <div className="float-left -mt-2">
      <span className="mx-7">Aspect Ratio:</span>
      <div className="inline-block">
        {Object.keys(availableAspectRatios).map((key, idx) => (
          <button
            key={idx}
            className={`mr-2 px-2 bg-indigo-100 rounded hover:bg-blue hover:opacity-90 hover:text-gray-200 transition-all transform ${
              aspectRatio === key && 'font-bold bg-blue text-white'
            }  hover:font-bold duration-2000`}
            onClick={() => {
              setAspectRatio(key);
            }}
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onOutsideModalClicked}
    >
      <div
        className="bg-white rounded-lg shadow-xl relative overflow-hidden transition-[width] duration-500"
        style={{
          width: `${ratio * 80}vh`,
          height: `${80}vh`,
        }}
      >
        <div className="flex border border-b-1 border-t-8 border-t-[#a5bef5] h-[52px] items-center bg-[#a5bef533] border-x-0">
          <div className="w-1/2 h-14 p-3 text-text-black text-1820 font-bold ml-4">
            Export Chart
          </div>
          <div className="w-1/2 h-14 p-3">
            <div className="items-center">
              <button
                onClick={onClose}
                className="absolute top-8 right-4 text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Modal content */}

        <div className="p-6 h-[calc(100%-52px)] flex flex-col">
          <div className="">{aspectRatioControl}</div>
          <div className="mt-5 flex-1 overflow-y-auto min-h-0">{children}</div>
        </div>
      </div>
    </div>
  );
};
